import type { EntryKey, UseQueryReturn } from "@pinia/colada";
import type { MaybeRefOrGetter } from "vue";
import type { Agent, AgentBody } from "@/schemas/agent.schema";
import { defineQuery, useMutation, useQuery, useQueryCache } from "@pinia/colada";
import { toValue } from "vue";
import { agentsApi } from "@/api/agents.api";
import { useProjectStore } from "@/stores/project.store";
import { DEPLOYMENT_QUERY_KEYS } from "./deployments.query";
import { SUBSCRIPTION_LIMITS_QUERY_KEYS } from "./subscription-limits.query";

/**
 * Query keys factory for agents following Pinia Colada best practices
 *
 * @see https://pinia-colada.esm.dev/guide/query-keys.html
 */
export const AGENT_QUERY_KEYS = {
  root: ["agents"] as const,
  list: (projectId?: string | null, filters?: any) =>
    [...AGENT_QUERY_KEYS.root, "list", { projectId }, filters] as EntryKey,
  detail: (id: string) => [...AGENT_QUERY_KEYS.root, "detail", id] as const,
  internalTools: () => [...AGENT_QUERY_KEYS.root, "internal-tools"] as const,
};

/**
 * Return type for useAgentsQuery
 * Explicitly typed to avoid exposing internal Pinia Colada plugin symbols
 */
type AgentsQueryReturn = UseQueryReturn<Agent[], Error>;

/**
 * Query hook for fetching agents (without deployments)
 *
 * NOTE: This query now returns agents WITHOUT deployment relations for better performance.
 * Use useDeploymentsQueries() from deployments.query.ts to fetch deployments separately,
 * or use useAgentsWithDeployments() composable for automatic composition.
 *
 * Features:
 * - **Project filtering**: Automatically filters by selected project via X-Project-ID header
 * - Lightweight query without joins
 * - Smart caching with 30s stale time
 * - Automatic deduplication of concurrent requests
 * - Shared cache across components
 * - Background refetch on window focus
 * - Query refetches when selected project changes
 *
 * @example
 * ```ts
 * const agentsQuery = useAgentsQuery();
 *
 * // In template
 * if (agentsQuery.isLoading.value) return <Spinner />;
 * const agents = agentsQuery.data.value; // Agent[] (without deploys)
 * ```
 */
export function agentsListQueryOptions() {
  const projectStore = useProjectStore();

  return {
    // Include project ID in key so query refetches when project changes
    key: () => AGENT_QUERY_KEYS.list(projectStore.currentProjectId) as EntryKey,
    query: async () => {
      // Pass project header to filter by selected project
      return await agentsApi.get({
        headers: projectStore.getProjectHeader(),
      });
    },

    staleTime: 30_000, // 30 seconds - data is considered fresh
    gcTime: 5 * 60_000, // 5 minutes - garbage collection time
  };
}

export const useAgentsQuery: () => AgentsQueryReturn = defineQuery(() =>
  useQuery(agentsListQueryOptions())
);

/**
 * Query hook for fetching ALL agents across the organization (no project filter).
 * Used in org analytics and global views where agents from all projects need name resolution.
 */
export const useOrgAgentsQuery: () => AgentsQueryReturn = defineQuery(() => {
  const query = useQuery({
    key: () => [...AGENT_QUERY_KEYS.root, "list", { scope: "org" }] as EntryKey,
    query: async () => {
      // No project header = all agents from all projects
      return await agentsApi.get();
    },
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });

  return query;
});

// ==================== INTERNAL TOOLS CATALOG ====================

/**
 * Query hook for the server internal-tools catalog (GET /agents/internal-tools).
 *
 * The catalog is the source of truth for valid internal tool names and their
 * deprecation flags. Consumers must fall back to a static list when the
 * endpoint is unreachable so the picker never renders empty.
 *
 * @example
 * ```ts
 * const catalogQuery = useInternalToolsQuery();
 * const tools = computed(() => catalogQuery.data.value ?? []);
 * ```
 */
export function useInternalToolsQuery() {
  return useQuery({
    key: AGENT_QUERY_KEYS.internalTools() as unknown as EntryKey,
    query: () => agentsApi.listInternalTools(),
    staleTime: 1000 * 60 * 30, // 30 minutes - catalog rarely changes
    gcTime: 1000 * 60 * 60, // 1 hour
  });
}

// ==================== AGENT DETAIL ====================

/**
 * Query for single agent (without deployments)
 *
 * Used in edit view and detail pages where you need a specific agent.
 * For deployments, use deployment queries separately based on what you need.
 *
 * NOTE: Detail queries don't filter by project since we're fetching by ID.
 * The agent's project_id is included in the response for display purposes.
 *
 * @param id - Agent ID (can be reactive ref or getter)
 *
 * @example
 * ```ts
 * const agentId = computed(() => route.params.agent_id as string);
 * const agentQuery = useAgentQuery(agentId);
 *
 * // If you need deployments:
 * const sipDeploysQuery = useSipDeploysQuery();
 * const agentSipDeploy = computed(() =>
 *   sipDeploysQuery.data.value?.find(d => d.agent_id === agentId.value)
 * );
 * ```
 */
export function useAgentQuery(id: MaybeRefOrGetter<string>) {
  const projectStore = useProjectStore();

  return useQuery({
    key: () => AGENT_QUERY_KEYS.detail(toValue(id)) as unknown as EntryKey,
    query: async () => {
      // Pass project header for access control
      return await agentsApi.getById(toValue(id), {
        headers: projectStore.getProjectHeader(),
      });
    },
    staleTime: 30_000,
    gcTime: 10 * 60_000,
  });
}

// ==================== AGENT CRUD MUTATIONS ====================

/**
 * Create new agent
 *
 * Automatically invalidates agents list after creation.
 * Uses project header to create agent in the selected project.
 *
 * @example
 * ```ts
 * const createMutation = useCreateAgent();
 *
 * const newAgent = await createMutation.mutateAsync({
 *   name: "My Agent",
 *   llm_model: "gpt-4",
 *   // ... other fields
 * });
 * ```
 */
export function useCreateAgent() {
  const queryCache = useQueryCache();
  const projectStore = useProjectStore();

  return useMutation({
    mutation: async (payload: AgentBody) => {
      // Pass project header to create agent in selected project
      return await agentsApi.create(payload, {
        headers: projectStore.getProjectHeader(),
      });
    },
    onSuccess: () => {
      // Invalidate list to refetch all agents
      queryCache.invalidateQueries({ key: AGENT_QUERY_KEYS.root as any });
      queryCache.invalidateQueries({ key: SUBSCRIPTION_LIMITS_QUERY_KEYS.root as any });
    },
  });
}

/**
 * Update existing agent
 *
 * Automatically invalidates both the specific agent and the list.
 * Uses project header for access control.
 *
 * @example
 * ```ts
 * const updateMutation = useUpdateAgent();
 *
 * await updateMutation.mutateAsync({
 *   id: "agent-123",
 *   name: "Updated Name",
 *   // ... other fields
 * });
 * ```
 */
export function useUpdateAgent() {
  const queryCache = useQueryCache();
  const projectStore = useProjectStore();

  return useMutation({
    mutation: async (payload: { id: string } & Partial<AgentBody>) => {
      const { id, ...body } = payload;
      return await agentsApi.updateById(id, body as AgentBody, {
        headers: projectStore.getProjectHeader(),
      });
    },
    onSuccess: (_data, variables) => {
      // Invalidate the specific agent detail
      queryCache.invalidateQueries({
        key: AGENT_QUERY_KEYS.detail(variables.id) as any,
      });
      // Invalidate the list (all project variants)
      queryCache.invalidateQueries({ key: AGENT_QUERY_KEYS.root as any });
    },
  });
}

/**
 * Delete agent
 *
 * Automatically invalidates agents list AND all deployment queries after deletion.
 * This ensures that deployment badges are updated when an agent is removed.
 * Uses project header for access control.
 *
 * @example
 * ```ts
 * const deleteMutation = useDeleteAgent();
 *
 * await deleteMutation.mutateAsync("agent-123");
 * ```
 */
export function useDeleteAgent() {
  const queryCache = useQueryCache();
  const projectStore = useProjectStore();

  return useMutation({
    mutation: async (id: string) => {
      return await agentsApi.deleteById(id, {
        headers: projectStore.getProjectHeader(),
      });
    },
    onSuccess: () => {
      // Invalidate all agent queries (list and details)
      queryCache.invalidateQueries({ key: AGENT_QUERY_KEYS.root as any });

      // CROSS-INVALIDATION: Also invalidate all deployment queries
      // When an agent is deleted, its deployments are also deleted by the backend
      // We need to refresh deployment lists to reflect this
      queryCache.invalidateQueries({ key: DEPLOYMENT_QUERY_KEYS.root as any });
      queryCache.invalidateQueries({ key: SUBSCRIPTION_LIMITS_QUERY_KEYS.root as any });
    },
  });
}

// ==================== HELPER HOOKS ====================

/**
 * Helper hook to invalidate agent queries
 *
 * Use this after mutations that affect agents (create, update, delete)
 *
 * @example
 * ```ts
 * const invalidate = useInvalidateAgents();
 *
 * async function createAgent(data) {
 *   await api.post('/agents', data);
 *   invalidate.all(); // Refetch all agent queries
 * }
 * ```
 */
export function useInvalidateAgents() {
  const queryCache = useQueryCache();

  return {
    /**
     * Invalidate all agent queries
     */
    all: () => queryCache.invalidateQueries({ key: AGENT_QUERY_KEYS.root as any }),

    /**
     * Invalidate only list queries
     */
    lists: () => queryCache.invalidateQueries({ key: [...AGENT_QUERY_KEYS.root, "list"] as any }),
  };
}
